/**
 * Environment configuration utilities
 */

export interface EnvironmentConfig {
  // Application
  APP_NAME: string
  APP_VERSION: string

  // API Configuration
  API_BASE_URL: string
  AUTH_URL: string
  ACCOUNT_URL: string
  LIGONIER_ORG_URL: string
  DONATE_URL: string

  // Development
  DEBUG_MODE: boolean
  LOG_LEVEL: 'debug' | 'info' | 'warn' | 'error'

  // Environment Detection
  NODE_ENV: string
  IS_PRODUCTION: boolean
  IS_DEVELOPMENT: boolean
  IS_BROWSER: boolean
}

/**
 * Get environment variable with fallback
 */
function getEnvVar(key: string, fallback: string = ''): string {
  // In browser/Vite context
  if (typeof import.meta !== 'undefined' && import.meta.env) {
    return import.meta.env[key] ?? fallback
  }

  // Fallback for other contexts
  if (typeof process !== 'undefined' && process.env) {
    return process.env[key] ?? fallback
  }

  return fallback
}

function getBooleanEnv(key: string, fallback: boolean = false): boolean {
  const value = getEnvVar(key, String(fallback))
  return value === 'true' || value === '1'
}

/**
 * Parse and validate environment configuration
 */
function createEnvironmentConfig(): EnvironmentConfig {
  const nodeEnv = getEnvVar('NODE_ENV', 'development')

  return {
    // Application
    APP_NAME: getEnvVar('VITE_APP_NAME', 'Ligonier Web Components'),
    APP_VERSION: getEnvVar('VITE_APP_VERSION', '1.0.0'),


    // API Configuration
    API_BASE_URL: getEnvVar('VITE_API_BASE_URL', 'https://apis.ligonier.org/v1'),
    AUTH_URL: getEnvVar('VITE_AUTH_BASE_URL', 'https://auth.ligonier.org'),
    ACCOUNT_URL: getEnvVar('VITE_ACCOUNT_BASE_URL', 'https://account.ligonier.org'),
    LIGONIER_ORG_URL: getEnvVar('VITE_LIGONIER_ORG_URL', 'https://www.ligonier.org'),
    DONATE_URL: getEnvVar('VITE_DONATE_URL', 'https://donate.ligonier.org'),

    // Development
    DEBUG_MODE: getBooleanEnv('VITE_DEBUG_MODE', nodeEnv === 'development'),
    LOG_LEVEL: (getEnvVar('VITE_LOG_LEVEL', 'info') as any) || 'info',

    // Environment Detection
    NODE_ENV: nodeEnv,
    IS_PRODUCTION: nodeEnv === 'production',
    IS_DEVELOPMENT: nodeEnv === 'development',
    IS_BROWSER: typeof window !== 'undefined',
  }
}

/**
 * Global environment configuration
 */
export const env: EnvironmentConfig = createEnvironmentConfig()

export function normalizeAuthBaseUrl(rawUrl: string, fallback: string = 'https://auth.ligonier.org'): string {
  const trimmedUrl = rawUrl.trim()
  if (!trimmedUrl) {
    return fallback
  }

  try {
    const url = new URL(trimmedUrl)
    const protocol = url.protocol.toLowerCase()
    if (protocol !== 'http:' && protocol !== 'https:') {
      return fallback
    }
    return url.origin
  } catch {
    return fallback
  }
}

/**
 * Get API configuration
 */
let _cachedApiConfig: ReturnType<typeof _buildApiConfig> | null = null

function _buildApiConfig() {
  const authBaseUrl = normalizeAuthBaseUrl(env.AUTH_URL)
  const accountBaseUrl = normalizeAuthBaseUrl(env.ACCOUNT_URL, 'https://account.ligonier.org')

  return {
    baseUrl: env.API_BASE_URL,
    authBaseUrl,
    authUrl: `${authBaseUrl}/api`,
    accountBaseUrl,
    ligonierOrgUrl: env.LIGONIER_ORG_URL,
    donateUrl: env.DONATE_URL,
    debugMode: env.DEBUG_MODE,
    logLevel: env.LOG_LEVEL,
  }
}

export function getApiConfig() {
  if (!_cachedApiConfig) {
    _cachedApiConfig = Object.freeze(_buildApiConfig())
  }
  return _cachedApiConfig
}

/**
 * Validate required environment variables
 */
export function validateEnvironment(): { isValid: boolean; errors: string[] } {
  const errors: string[] = []

  // Check required variables

  if (!env.API_BASE_URL) {
    errors.push('VITE_API_BASE_URL is required')
  }

  return {
    isValid: errors.length === 0,
    errors
  }
}

/**
 * Log environment configuration (excluding sensitive data)
 */
export function logEnvironmentInfo() {
  if (!env.DEBUG_MODE) return

  console.info('Environment Configuration:', {
    APP_NAME: env.APP_NAME,
    APP_VERSION: env.APP_VERSION,
    NODE_ENV: env.NODE_ENV,
    IS_PRODUCTION: env.IS_PRODUCTION,
    IS_DEVELOPMENT: env.IS_DEVELOPMENT,
    IS_BROWSER: env.IS_BROWSER,
    DEBUG_MODE: env.DEBUG_MODE,
    LOG_LEVEL: env.LOG_LEVEL,
    API_BASE_URL: env.API_BASE_URL,
    // Note: Sensitive data like tokens are not logged
  })
}